import React, { useEffect, useState } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import Get from '../../api/internal/Get';
import Button from '../../assets/essentials/Button';
import Input from '../../assets/essentials/Input';
import Select from '../../assets/essentials/Select';
import BBITable from '../../helpers/bBITable/BBITable';
import FilterData from '../../helpers/filter/FilterData';
import OptionList from '../../helpers/options/OptionList';
import View from '../../helpers/slab/View';
import ViewTrailer from '../../features/views/assets/trailer/Trailer';
import classesCustom from '../../helpers/search/CustomSearch.module.css';
import { PageLoadingSpinner } from '../../helpers/loadingSpinner/LoadingSpinner';

function SearchTrailer() {
  const [trailers, setTrailers] = useState<TrailerBoard[]>([]);
  const [searchTrailerParams, setSearchTrailerParams] = useSearchParams();
  const [trailerOptions, setTrailerOptions] =
    useState<CreateTrailerOptions | null>();
  const [isLoading, setIsLoading] = useState(true);

  const filterValue = useOutletContext();

  useEffect(() => {
    if (trailerOptions) {
      return;
    }
    Get(`/Asset/GetCreateTrailer`).then(response => {
      if (response) {
        setTrailerOptions(response.data);
      }
    });
  }, [trailerOptions]);

  useEffect(() => {
    setIsLoading(true);
    Get(`/Asset/SearchTrailers?${searchTrailerParams.toString()}`)
      .then(response => {
        if (response) {
          setTrailers(response.data);
          setIsLoading(false);
        }
      })
      .finally(() => setIsLoading(false));
  }, [searchTrailerParams]);

  const handleFormSubmit = event => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const newParams = new URLSearchParams();
    formData.forEach((value, key) => {
      if (value !== '') {
        newParams.set(key, value.toString());
      }
    });
    setSearchTrailerParams(newParams, { replace: true });
  };

  const columnConfig = [
    {
      key: '1',
      attribute: 'trailerName',
      attributeprimaryhidden: 'assetId',
      header: 'Trailer Unit #',
      onClick: e =>
        View(
          <ViewTrailer
            trailerName={e.target.innerText}
            assetId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '2',
      attribute: 'vin',
      header: 'VIN',
    },
    {
      key: '3',
      attribute: 'make',
      header: 'Make',
    },
    {
      key: '4',
      attribute: 'model',
      header: 'Model',
    },
    {
      key: '5',
      attribute: 'year',
      header: 'Year',
    },
    {
      key: '6',
      attribute: 'licensePlateNumber',
      header: 'Plate',
    },
    {
      key: '7',
      attribute: 'licensePlateState',
      header: 'Plate State',
    },
    {
      key: '8',
      attribute: 'ownership',
      header: 'Ownership',
    },
    {
      key: '9',
      attribute: 'trailerType',
      header: 'Type',
    },
        {
      key: '10',
      attribute: 'driverName',
      header: 'Driver',
    },
    {
      key: '11',
      attribute: 'weeklyCost',
      header: 'Weekly Cost',
      dataType: 'currency',
    },
  ];

  const tableData = FilterData(trailers, filterValue);

  return (
    <>
      {trailerOptions && (
        <div className={`${classesCustom.customSearch}`}>
          <form onSubmit={handleFormSubmit}>
            {/* Trailer Unit # */}
            <label>
              Trailer Unit #
              <Input
                type="text"
                name="trailerName"
                defaultValue={searchTrailerParams.get('trailerName') ?? ''}
              />
            </label>
            {/* VIN */}
            <label>
              VIN
              <Input
                type="text"
                name="vin"
                defaultValue={searchTrailerParams.get('vin') ?? ''}
              />
            </label>
            {/* Make */}
            <label>
              Make
              <Input
                type="text"
                name="make"
                defaultValue={searchTrailerParams.get('make') ?? ''}
              />
            </label>
            {/* Model */}
            <label>
              Model
              <Input
                type="text"
                name="model"
                defaultValue={searchTrailerParams.get('model') ?? ''}
              />
            </label>
            {/* Year */}
            <label>
              Year
              <Input
                type="text"
                name="year"
                defaultValue={searchTrailerParams.get('year') ?? ''}
              />
            </label>
            {/* Plate */}
            <label>
              Plate
              <Input
                type="text"
                name="licensePlateNumber"
                defaultValue={
                  searchTrailerParams.get('licensePlateNumber') ?? ''
                }
              />
            </label>
            {/* Ownership */}
            <label htmlFor="ownershipTypeId">
              Ownership
              <Select
                id="ownershipTypeId"
                name="ownershipTypeId"
                defaultValue={searchTrailerParams.get('ownershipTypeId') ?? ''}
              >
                <option value="">-- Any --</option>
                <OptionList
                  optionData={trailerOptions?.ownershipTypeOptions}
                  attributeID="ownershipTypeId"
                  attributeName="name"
                />
              </Select>
            </label>
            {/* Type */}
            <label>
              Type
              <Select
                id="trailerTypeId"
                name="trailerTypeId"
                defaultValue={searchTrailerParams.get('trailerTypeId') ?? ''}
              >
                <option value="">-- Any --</option>
                <OptionList
                  optionData={trailerOptions?.trailerTypeOptions}
                  attributeID="trailerTypeId"
                  attributeName="type"
                  attributeGroup="group"
                />
              </Select>
            </label>
            {/* Asset Status */}
            <label>
              Asset Status
              <Select
                name="isActive"
                defaultValue={searchTrailerParams.get('isActive') ?? ''}
              >
                <option value="">-- Any --</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </label>
            <div className={classesCustom.submitHolder}>
              <Button type="submit" variant="good">
                Search
              </Button>
              <Button
                onClick={() => setSearchTrailerParams({})}
                type="reset"
                variant="bad"
              >
                Clear
              </Button>
            </div>
          </form>
        </div>
      )}
      {isLoading ? (
        <PageLoadingSpinner />
      ) : (
        <BBITable data={tableData} columns={columnConfig} />
      )}
    </>
  );
}

export default SearchTrailer;
